import { definedMap, withDefault } from "collection-utils";

import { SerializedComponent } from "../../components/SerializedApp";
import { getDocURL } from "../../docUrl";
import { assert } from "../../Support";
import { makeScreenContextForActions } from "../actions";
import { makeText } from "../builder-utils";
import {
    ActionComponentDescription,
    ComponentKind,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    TableColumn,
    TableGlideType,
    getTableColumnDisplayName,
    isPrimitiveType,
    makeEmptyComponentDescription,
} from "../description";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { makeColumnProperty, makeStringProperty } from "./property-utils";
import { ComponentBuilder, ComponentConfiguratorContext, ComponentDescriptor } from ".";
import { ActionComponentHandlerBase } from "./action-component";

const ComponentKindPhoneNumber: ComponentKind = "phone-number";

const withMessagePropertyHandler = new SwitchPropertyHandler(
    { withMessage: true },
    "Allow text messages",
    PropertySection.Design
);

const withDialPropertyHandler = new SwitchPropertyHandler(
    { withDial: true },
    "Allow phone calls",
    PropertySection.Design
);

interface PhoneNumberComponentDescription extends ActionComponentDescription {
    readonly propertyName: LegacyPropertyDescription;
    readonly messageBody: LegacyPropertyDescription | undefined;
    readonly caption: LegacyPropertyDescription | undefined;
}

export class PhoneNumberComponentHandler extends ActionComponentHandlerBase<PhoneNumberComponentDescription> {
    constructor() {
        super(ComponentKindPhoneNumber);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: PhoneNumberComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const phoneNumber = makeText(builder, desc.propertyName, tables.input, true, false);
        const phoneNumberWithFormat = makeText(builder, desc.propertyName, tables.input, true, true);
        if (phoneNumber === undefined) return b.emptyUI;
        assert(phoneNumberWithFormat !== undefined);

        const onPhoneCall: SerializedComponent[] = [];
        const onTextMessage: SerializedComponent[] = [];

        const { actions } = this.getActions(desc, tables.input, builder, mutatingScreenKind);
        const { lets, screenContext } = makeScreenContextForActions(builder, actions, tables, mutatingScreenKind);
        const actionOnTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );
        if (actionOnTap !== undefined) {
            onPhoneCall.push(actionOnTap);
            onTextMessage.push(actionOnTap);
        }

        const messageBody = makeText(builder, desc.messageBody, tables.input);

        onPhoneCall.push(b.phoneCall(phoneNumber));

        const onTextMessageLets: [string, SerializedComponent][] = [];

        if (messageBody !== undefined) {
            onTextMessageLets.push(["body", messageBody]);
        }
        onTextMessage.push(b.textMessage(phoneNumber, messageBody === undefined ? undefined : b.getVariable("body")));
        let textMessageSequence = b.sequence(onTextMessage);
        if (onTextMessageLets.length > 0) {
            textMessageSequence = b.let(onTextMessageLets, textMessageSequence);
        }

        const withMessage = withMessagePropertyHandler.getSwitch(desc);
        const withDial = withDialPropertyHandler.getSwitch(desc);

        const child = b.ui("PhoneNumber", {
            number: phoneNumberWithFormat,
            caption: makeText(builder, desc.caption, tables.input, true),
            onPhoneCall: withDial ? b.sequence(onPhoneCall) : undefined,
            onTextMessage: withMessage ? textMessageSequence : undefined,
        });
        const ui = b.ifIsNotEmpty(b.getContext, child);
        return b.let(lets, ui);
    }

    public getDescriptor(
        desc: PhoneNumberComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<PhoneNumberComponentDescription> {
        const withMessage = withDefault(
            definedMap(desc, d => withMessagePropertyHandler.getSwitch(d)),
            true
        );

        const properties = [
            makeTextPropertyDescriptor("propertyName", "Column", "Phone number", true, mutatingScreenKind, {
                isDefaultCaption: true,
                columnFirst: true,
            }),
            makeCaptionStringPropertyDescriptor("Phone", false, mutatingScreenKind),
            withDialPropertyHandler,
            withMessagePropertyHandler,
            ...this.getBasePropertyDescriptors(),
        ];

        if (withMessage) {
            properties.splice(
                2,
                0,
                makeTextPropertyDescriptor("messageBody", "Message body", "SMS text", false, mutatingScreenKind, {
                    emptyByDefault: true,
                    searchable: false,
                    columnFirst: true,
                    propertySection: PropertySection.Design,
                })
            );
        }

        return {
            name: "Phone",
            description: "A phone number that you can call or text",
            img: "componentPhone",
            group: "Buttons",
            helpUrl: getDocURL("phoneNumber"),
            properties,
        };
    }

    public static defaultComponent(column: TableColumn): PhoneNumberComponentDescription {
        assert(isPrimitiveType(column.type));

        // FIXME: It would be nice if we could have this statically typed.
        return {
            ...makeEmptyComponentDescription(ComponentKindPhoneNumber),
            actions: [],
            propertyName: makeColumnProperty(column.name),
            messageBody: undefined,
            caption: makeStringProperty(getTableColumnDisplayName(column)),
        };
    }
}
